<template>
  <v-card class="pa-4">
    <v-row dense align="stretch">
      <!-- status perhitungan -->
      <v-col cols="12" md="4" class="d-flex flex-column justify-center pr-md-4 bm-run">
        <div class="bm-tiny">{{ running ? "Perhitungan berjalan" : "Perhitungan terakhir" }}</div>
        <div v-if="!lastRun" class="subtitle-1 font-weight-medium">Belum ada perhitungan</div>
        <template v-else-if="running">
          <div class="subtitle-1 font-weight-medium d-flex align-center bm-ell">
            <span class="bm-dot primary mr-2"></span>
            Menghitung…
            <span v-if="lastRun.jobCount" class="ml-1"
              >{{ lastRun.processedJobs || 0 }} / {{ lastRun.jobCount }} job</span
            >
          </div>
          <v-progress-linear
            :value="progress === null ? 0 : progress * 100"
            :indeterminate="progress === null"
            color="primary"
            height="4"
            rounded
            class="my-1"
          ></v-progress-linear>
          <div class="bm-tiny bm-ell">
            Mulai {{ formatDateTime(lastRun.startedAt) }} · data sebelumnya tetap ditampilkan sampai
            selesai
          </div>
        </template>
        <template v-else>
          <div
            class="subtitle-1 font-weight-medium d-flex align-center bm-ell"
            :class="{ 'error--text': failed }"
          >
            <span class="bm-dot mr-2" :class="statusColor"></span>
            {{ statusText }} · {{ formatDateTime(lastRun.finishedAt || lastRun.startedAt) }}
          </div>
          <div class="bm-tiny bm-ell" :title="lastRun.errorSummary || ''">
            <template v-if="failed">{{ firstError }}</template>
            <template v-else>
              {{ lastRun.poCount || 0 }} PO · {{ lastRun.jobCount || 0 }} job
              <span v-if="duration"> · {{ duration }}</span>
              <span v-if="lastRun.errorSummary" class="warning--text"> · ada catatan</span>
            </template>
          </div>
        </template>
      </v-col>

      <!-- jumlah PO per band: klik = filter -->
      <v-col v-for="b in bands" :key="b.key" cols="6" md="2">
        <div
          class="bm-kpi-box"
          :class="{ 'bm-kpi-box--active': activeBand === b.key }"
          :style="{ borderColor: b.hex, background: b.tint }"
          role="button"
          tabindex="0"
          @click="$emit('band', activeBand === b.key ? '' : b.key)"
          @keyup.enter="$emit('band', activeBand === b.key ? '' : b.key)"
        >
          <div class="bm-cap d-flex align-center">
            <span class="bm-dot mr-1" :style="{ background: b.hex }"></span>{{ b.label }}
          </div>
          <div class="bm-kpi-value">{{ counts[b.key] }}</div>
          <div class="bm-tiny bm-ell">
            {{ b.hint
            }}<template v-if="predicted[b.key]">
              ·
              <strong :title="predictedTitle(b.key)">+{{ predicted[b.key] }}</strong>
              prediksi</template
            >
          </div>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import {
  bandCounts,
  bandHex,
  formatDateTime,
  predictedCounts,
  runProgress,
} from "@/utils/budgetMonitor";

/** Header strip of the list: last run (with progress while RUNNING) + PO count per band. */
export default {
  name: "budget-summary-strip",
  props: {
    summary: { type: Object, default: null },
    lastRun: { type: Object, default: null },
    activeBand: { type: String, default: "" },
  },
  data: () => ({
    bands: [
      {
        key: "RED",
        label: "MERAH · > 100 %",
        hint: "PO perlu tindakan",
        hex: bandHex("RED"),
        tint: "#FFF5F5",
      },
      {
        key: "YELLOW",
        label: "KUNING · 80–100 %",
        hint: "PO mendekati batas",
        hex: bandHex("YELLOW"),
        tint: "#FFFBEA",
      },
      {
        key: "GREEN",
        label: "HIJAU · ≤ 80 %",
        hint: "PO aman",
        hex: bandHex("GREEN"),
        tint: "#F3FBF4",
      },
      {
        key: "NO_BUDGET",
        label: "TANPA BUDGET",
        hint: "margin CRM kosong",
        hex: bandHex("NO_BUDGET"),
        tint: "#FAFAFA",
      },
    ],
  }),
  computed: {
    counts() {
      return bandCounts(this.summary);
    },
    /** POs whose projected band (sisa BOM × harga terakhir) is worse than today's. */
    predicted() {
      return predictedCounts(this.summary);
    },
    running() {
      return !!this.lastRun && this.lastRun.status === "RUNNING";
    },
    failed() {
      return !!this.lastRun && this.lastRun.status === "FAILED";
    },
    progress() {
      return runProgress(this.lastRun);
    },
    statusText() {
      const s = this.lastRun && this.lastRun.status;
      return s === "DONE"
        ? "Selesai"
        : s === "PARTIAL"
        ? "Selesai sebagian"
        : s === "FAILED"
        ? "Gagal"
        : s;
    },
    statusColor() {
      const s = this.lastRun && this.lastRun.status;
      return s === "DONE" ? "success" : s === "PARTIAL" ? "warning" : "error";
    },
    firstError() {
      const e = (this.lastRun && this.lastRun.errorSummary) || "";
      return e.split("\n")[0] || "Perhitungan gagal";
    },
    duration() {
      if (!this.lastRun || !this.lastRun.startedAt || !this.lastRun.finishedAt) return "";
      const s = Math.round(
        (new Date(this.lastRun.finishedAt) - new Date(this.lastRun.startedAt)) / 1000
      );
      return s < 0 ? "" : s < 90 ? `${s} detik` : `${Math.round(s / 60)} menit`;
    },
  },
  methods: {
    predictedTitle(band) {
      return band === "RED"
        ? "PO hijau / kuning yang diproyeksikan melebihi budget bila sisa BOM dibeli dengan harga terakhir"
        : "PO hijau yang diproyeksikan mendekati batas bila sisa BOM dibeli dengan harga terakhir";
    },
    formatDateTime,
  },
};
</script>

<style scoped>
.bm-run {
  border-right: thin solid rgba(0, 0, 0, 0.12);
  min-width: 0;
}
.bm-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}
.bm-kpi-box {
  height: 100%;
  border: thin solid;
  border-radius: 4px;
  padding: 10px 14px;
  cursor: pointer;
  min-width: 0;
  outline: none;
}
.bm-kpi-box--active {
  box-shadow: 0 0 0 2px #1976d2 inset;
}
.bm-kpi-box:focus {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.5) inset;
}
</style>
